import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TABS = ["Profile", "Assessments", "Reports", "Timeline", "Consultations", "Communication"];

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

const Row = ({ label, value }) => (
  <div className="flex flex-wrap justify-between gap-2 border-b border-forest/8 py-3 text-sm">
    <span className="text-taupe">{label}</span>
    <span className="font-semibold text-charcoal/85">{value ?? "—"}</span>
  </div>
);

export default function AdminMemberDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Profile");
  const [deleting, setDeleting] = useState(false);
  const [fullReport, setFullReport] = useState(null);

  useEffect(() => {
    document.title = "Member Detail | Rooted Healing Admin";
    api
      .get(`/admin/members/${userId}`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail, "Could not load member."));
        setData(false);
      });
  }, [userId]);

  const viewAssessment = async (aid) => {
    try {
      const { data } = await api.get(`/admin/members/${userId}/assessments/${aid}`);
      setFullReport(data);
      toast.success("Full report opened. This view was recorded in the audit log.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteMember = async () => {
    if (!window.confirm("Permanently delete this member and all of their wellness data? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/members/${userId}`);
      toast.success("Member and all associated data deleted.");
      navigate("/admin/members");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  const m = data.member;
  const bookingClicks = data.events.filter((e) => e.event === "booking_click");

  return (
    <div data-testid="admin-member-detail">
      <Link
        to="/admin/members"
        data-testid="member-detail-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-forest underline-offset-4 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> All members
      </Link>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-forest">{m.name}</h1>
          <p className="mt-1 text-sm text-taupe">{m.email}</p>
        </div>
        <div className="flex gap-3">
          <a
            data-testid="member-export-csv"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/members/${userId}/export.csv`}
            className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-2.5 text-sm font-semibold text-forest hover:border-gold"
          >
            <Download className="h-4 w-4" /> Export contact CSV
          </a>
          {m.role !== "admin" && (
            <button
              data-testid="member-delete-button"
              onClick={deleteMember}
              disabled={deleting}
              className="inline-flex items-center gap-2 rounded-full border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" /> Delete member data
            </button>
          )}
        </div>
      </div>

      <div className="mt-7 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`member-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === t ? "bg-forest text-ivory" : "border border-forest/15 text-charcoal/70 hover:bg-sage"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-[1.75rem] border border-forest/10 bg-white/75 p-7">
        {tab === "Profile" && (
          <div data-testid="member-tab-panel-profile" className="max-w-xl">
            <Row label="Full name" value={m.name} />
            <Row label="Email" value={m.email} />
            <Row label="Phone" value={m.phone || "—"} />
            <Row label="Sign-in method" value={m.provider} />
            <Row label="Role" value={m.role || "member"} />
            <Row label="Registered" value={fmt(m.created_at)} />
            <Row label="Last sign-in" value={fmt(m.last_login_at)} />
            <Row label="Account status" value={m.status || "active"} />
          </div>
        )}

        {data.sensitive_restricted && tab !== "Profile" && (
          <p
            data-testid="member-sensitive-restricted"
            className="rounded-2xl bg-sage/60 px-5 py-4 text-xs leading-relaxed text-charcoal/70"
          >
            Your role does not include access to wellness responses, reports, or intentions.
            Ask the Owner if this member needs that level of support.
          </p>
        )}

        {tab === "Assessments" && (
          <div data-testid="member-tab-panel-assessments">
            {data.assessments.length === 0 ? (
              <p className="text-sm text-taupe">No completed discoveries yet.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {data.assessments.map((a) => (
                  <div
                    key={a.assessment_id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-forest/10 px-5 py-4"
                  >
                    <div>
                      <p className="text-sm font-semibold text-forest">{fmt(a.created_at)}</p>
                      <p className="text-xs text-taupe">
                        {a.results?.overall} · scoring {a.scoring_version}
                      </p>
                    </div>
                    <button
                      data-testid={`member-view-assessment-${a.assessment_id}`}
                      onClick={() => viewAssessment(a.assessment_id)}
                      className="text-xs font-semibold text-gold-dark underline-offset-4 hover:underline"
                    >
                      Open full responses & report
                    </button>
                  </div>
                ))}
              </div>
            )}
            {fullReport && (
              <div className="mt-6 rounded-2xl bg-sage/50 p-6" data-testid="member-full-report">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                  Sensitive record · audit logged
                </p>
                <p className="mt-3 font-serif text-lg text-forest">
                  Overall: {fullReport.results.overall}
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(fullReport.results.dimensions).map(([cat, d]) => (
                    <p key={cat} className="text-sm text-charcoal/75">
                      <span className="font-semibold">{fullReport.category_names[cat]}:</span> {d.label}
                    </p>
                  ))}
                </div>
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-semibold text-forest">
                    Original answers ({Object.keys(fullReport.results.dimension_entries ? Object.values(fullReport.results.dimension_entries).flat().length : 0)})
                  </summary>
                  <ul className="mt-3 flex flex-col gap-2">
                    {Object.values(fullReport.results.dimension_entries || {})
                      .flat()
                      .map((e) => (
                        <li key={e.answer_id} className="text-xs text-charcoal/70">
                          <span className="text-taupe">{e.question_text}</span>{" "}
                          <span className="font-semibold text-forest">{e.answer_text}</span>
                        </li>
                      ))}
                  </ul>
                </details>
              </div>
            )}
          </div>
        )}

        {tab === "Reports" && (
          <div data-testid="member-tab-panel-reports">
            {data.assessments.length === 0 ? (
              <p className="text-sm text-taupe">No reports yet.</p>
            ) : (
              <p className="text-sm leading-relaxed text-charcoal/70">
                {data.assessments.length} extended report(s) exist. Open them from the
                Assessments tab; every view is recorded in the audit log.
              </p>
            )}
          </div>
        )}

        {tab === "Timeline" && (
          <div data-testid="member-tab-panel-timeline" className="flex flex-col gap-3">
            {data.assessments.length === 0 ? (
              <p className="text-sm text-taupe">No timeline entries yet.</p>
            ) : (
              [...data.assessments].reverse().map((a) => (
                <div key={a.assessment_id} className="flex items-center gap-4 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full bg-gold" aria-hidden="true" />
                  <span className="font-semibold text-forest">{fmt(a.created_at)}</span>
                  <span className="text-charcoal/70">{a.results?.overall}</span>
                  {a.intention?.text && (
                    <span className="truncate text-xs italic text-taupe">"{a.intention.text}"</span>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {tab === "Consultations" && (
          <div data-testid="member-tab-panel-consultations">
            <p className="text-sm text-charcoal/75">
              Scheduler link clicks recorded for this member: <strong>{bookingClicks.length}</strong>
            </p>
            <p className="mt-2 text-xs leading-relaxed text-taupe">
              Clicks are not verified appointments. Verified booking data requires a scheduling
              provider integration (see Consultations section).
            </p>
          </div>
        )}

        {tab === "Communication" && (
          <div data-testid="member-tab-panel-communication" className="max-w-xl">
            <Row label="Newsletter consent" value={m.consents?.newsletter ? "Yes" : "No"} />
            <Row label="Reminder consent" value={m.consents?.reminders ? "Yes" : "No"} />
            <Row label="Terms accepted" value={fmt(m.consents?.terms_accepted_at)} />
            <Row label="Reminder emails sent" value={data.reminders_sent.length} />
            <p className="mt-4 text-xs leading-relaxed text-taupe">
              Newsletter and reminder preferences are separate consents and were chosen by the
              member.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
